/**
 * Eligibility Error Message Mapping
 *
 * Maps Stedi AAA error codes to user-friendly error messages.
 * These codes come from X12 271 eligibility responses when a payer rejects an eligibility request.
 *
 * Reference: https://www.stedi.com/blog/aaa-eligibility-errors-in-plain-english
 */

// Common AAA error codes mapped to user-friendly messages
export const ELIGIBILITY_ERROR_MESSAGES: Record<
  string,
  { message: string; suggestion?: string }
> = {
  // Patient/Subscriber Identity Errors
  '58': {
    message: 'Invalid or missing date of birth.',
    suggestion:
      'Please verify the patient date of birth is correct and in the proper format.',
  },
  '64': {
    message: 'Invalid or missing patient ID.',
    suggestion: 'Please verify the Member ID/Policy Number is correct.',
  },
  '65': {
    message: 'Invalid or missing patient name.',
    suggestion:
      'Please verify the patient name matches what is on file with the insurance.',
  },
  '66': {
    message: 'Invalid or missing patient gender.',
    suggestion: 'Please verify the patient gender is correct.',
  },
  '67': {
    message: 'Patient not found in the insurance system.',
    suggestion:
      'Please verify the patient information matches exactly what is on their insurance card.',
  },
  '68': {
    message: 'Duplicate patient ID found.',
    suggestion:
      'Contact the insurance company to resolve the duplicate record issue.',
  },
  '69': {
    message: 'Patient information is inconsistent with their age.',
    suggestion: 'Please verify the date of birth and other patient details.',
  },
  '70': {
    message: "Patient gender does not match the insurance company's records.",
    suggestion:
      "Please verify the patient's gender matches their insurance records.",
  },
  '71': {
    message:
      "Patient date of birth does not match the insurance company's records.",
    suggestion:
      'Please verify the date of birth matches exactly what is on file with the insurance. Check for typos or formatting issues.',
  },
  '72': {
    message: 'Invalid or missing subscriber/member ID.',
    suggestion:
      'Please verify the Member ID matches exactly what is shown on the insurance card.',
  },
  '73': {
    message: 'Invalid or missing subscriber name.',
    suggestion:
      'Please verify the subscriber name matches the insurance records.',
  },
  '74': {
    message: 'Invalid or missing subscriber gender.',
    suggestion: 'Please verify the subscriber gender is correct.',
  },
  '75': {
    message: 'Subscriber not found in the insurance system.',
    suggestion:
      'Please verify the Member ID and subscriber information match the insurance card exactly.',
  },
  '76': {
    message: 'Duplicate subscriber ID found.',
    suggestion:
      'Contact the insurance company to resolve the duplicate record issue.',
  },
  '77': {
    message: 'Subscriber found but patient (dependent) not found.',
    suggestion:
      'Please verify the dependent information is correct and that they are enrolled under this policy.',
  },
  '78': {
    message: 'Subscriber not found in the specified group or plan.',
    suggestion:
      'Please verify the insurance plan and group number are correct.',
  },

  // Provider Errors
  '43': {
    message: 'Invalid or missing provider identification.',
    suggestion:
      'Please verify the clinic NPI is correct and the provider is enrolled with this payer.',
  },
  '44': {
    message: 'Invalid or missing provider name.',
    suggestion: 'Please verify the provider information is correct.',
  },
  '45': {
    message: 'Invalid or missing provider specialty.',
    suggestion: 'Please verify the provider specialty information.',
  },
  '46': {
    message: 'Invalid or missing provider phone number.',
    suggestion: 'Please verify the clinic contact information.',
  },
  '47': {
    message: 'Invalid or missing provider state.',
    suggestion: 'Please verify the clinic address information.',
  },
  '48': {
    message: 'Invalid or missing referring provider identification.',
    suggestion: 'Please verify the referring provider NPI.',
  },
  '49': {
    message: 'Provider is not a primary care physician.',
    suggestion:
      'This service may require a primary care physician. Please verify the provider type.',
  },
  '50': {
    message: 'Provider is not eligible to submit eligibility inquiries.',
    suggestion:
      'Please verify the provider is enrolled for eligibility checks with this payer.',
  },
  '51': {
    message: 'Provider not on file with the insurance company.',
    suggestion:
      'The provider may need to complete credentialing or enrollment with this payer.',
  },
  '52': {
    message:
      'Service dates are not within the provider enrollment period with this plan.',
    suggestion: "Please verify the provider's enrollment dates with the payer.",
  },

  // Date/Service Errors
  '56': {
    message: 'Inappropriate date provided.',
    suggestion: 'Please verify all dates are valid and appropriate.',
  },
  '57': {
    message: 'Invalid or missing date of service.',
    suggestion:
      'Please verify the service date is correct and properly formatted.',
  },
  '60': {
    message: 'Date of birth is after the date of service.',
    suggestion: 'Please verify the date of birth and service date are correct.',
  },
  '61': {
    message: 'Date of death is before the date of service.',
    suggestion: 'Please verify the patient records and service date.',
  },
  '62': {
    message: 'Date of service is not within the allowable inquiry period.',
    suggestion: 'The service date may be too far in the past to check.',
  },
  '63': {
    message: 'Date of service is in the future.',
    suggestion: 'Please use a current or past date for eligibility checks.',
  },

  // General/System Errors
  '04': {
    message: 'Authorization or access restrictions.',
    suggestion:
      'The provider may not be authorized to check eligibility for this payer.',
  },
  '15': {
    message: 'Required information is missing.',
    suggestion:
      'Please ensure all required patient and insurance information is provided.',
  },
  '33': {
    message: 'There were errors in the submitted information.',
    suggestion: 'Please review and correct the patient and insurance details.',
  },
  '35': {
    message: 'The provider is out of network.',
    suggestion: 'This provider may be out of network for this insurance plan.',
  },
  '41': {
    message: 'Authorization or access restrictions.',
    suggestion:
      'The provider may not be authorized for this type of eligibility inquiry.',
  },
  '42': {
    message: 'Unable to respond at this time.',
    suggestion:
      'The insurance system may be temporarily unavailable. Please try again later.',
  },
  '79': {
    message: 'Invalid participant identification.',
    suggestion:
      'Please verify the payer ID and insurance information are correct.',
  },
  '80': {
    message: 'No response received from the insurance company.',
    suggestion: 'The request timed out. Please try again in a few minutes.',
  },
  '96': {
    message: 'Service type code is not supported.',
    suggestion:
      'This payer may not support eligibility checks for this service type.',
  },
  '97': {
    message: 'Invalid service type code.',
    suggestion: 'Please verify the service type configuration.',
  },
  A0: {
    message: 'Invalid request format.',
    suggestion: 'Please contact support if this issue persists.',
  },
  A1: {
    message: 'Address information request is not supported.',
    suggestion:
      'This payer does not support address information in eligibility requests.',
  },
  A2: {
    message: 'Address information is not available.',
    suggestion:
      'The payer does not have address information on file for this member.',
  },
  A3: {
    message: 'Name spelling variations may exist.',
    suggestion:
      'Please verify the patient name spelling matches the insurance records exactly.',
  },
  A4: {
    message: 'Healthcare provider not found.',
    suggestion:
      'Please verify the provider NPI and that they are enrolled with this payer.',
  },
  A5: {
    message: 'Provider is not eligible for this type of submission.',
    suggestion:
      'Please verify the provider is enrolled for this transaction type.',
  },
  A6: {
    message: 'Prior authorization is required for this service.',
    suggestion:
      'A prior authorization may be needed before this service can be verified.',
  },
  A7: {
    message: 'Certification information does not match the patient.',
    suggestion: 'Please verify the certification details match the patient.',
  },
  A8: {
    message: 'Member ID does not match the prior authorization number.',
    suggestion:
      'Please verify the prior authorization is for the correct patient.',
  },
  T4: {
    message: 'Payer name or identifier is missing.',
    suggestion: 'Please verify the insurance payer ID is correct.',
  },
  Z0: {
    message: 'An unknown error occurred.',
    suggestion: 'Please try again or contact support if the issue persists.',
  },
};

// Generic fallback message for unmapped error codes
export const GENERIC_ERROR_MESSAGE = {
  message: 'The eligibility check could not be completed.',
  suggestion:
    'Please verify all patient and insurance information is correct and try again. If the problem persists, contact the insurance company directly.',
};

/**
 * Get a user-friendly error message for a Stedi AAA error
 */
export function getEligibilityErrorMessage(
  errorCode?: string | null,
  errorDescription?: string | null
): { message: string; suggestion?: string } {
  // If we have a mapped error code, use that
  if (errorCode && ELIGIBILITY_ERROR_MESSAGES[errorCode]) {
    return ELIGIBILITY_ERROR_MESSAGES[errorCode];
  }

  // If no code but we have a description, try to parse common patterns
  if (errorDescription) {
    const descLower = errorDescription.toLowerCase();

    // Patient date of birth mismatch
    if (
      descLower.includes('birth date') ||
      descLower.includes('date of birth')
    ) {
      if (descLower.includes('does not match')) {
        return ELIGIBILITY_ERROR_MESSAGES['71'];
      }
      return ELIGIBILITY_ERROR_MESSAGES['58'];
    }

    // Patient not found
    if (
      descLower.includes('patient not found') ||
      descLower.includes('member not found')
    ) {
      return ELIGIBILITY_ERROR_MESSAGES['67'];
    }

    // Subscriber not found
    if (descLower.includes('subscriber') && descLower.includes('not found')) {
      return ELIGIBILITY_ERROR_MESSAGES['75'];
    }

    // Provider not found
    if (descLower.includes('provider') && descLower.includes('not found')) {
      return ELIGIBILITY_ERROR_MESSAGES['51'];
    }

    // Gender mismatch
    if (descLower.includes('gender') && descLower.includes('does not match')) {
      return ELIGIBILITY_ERROR_MESSAGES['70'];
    }

    // Invalid member ID
    if (
      (descLower.includes('member id') ||
        descLower.includes('subscriber id')) &&
      (descLower.includes('invalid') || descLower.includes('missing'))
    ) {
      return ELIGIBILITY_ERROR_MESSAGES['72'];
    }
  }

  // Return generic message for unknown errors
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Extract and format error message from eligibility response
 * This handles both the AAA errors array and the error_message string
 */
export interface StediAAAError {
  code?: string | null;
  description?: string | null;
  followupActionCode?: string | null;
  location?: string | null;
  loop?: string | null;
  segment?: string | null;
  possibleResolutions?: string | null;
}

export function formatEligibilityErrorForDisplay(
  errorMessage?: string | null,
  errors?: StediAAAError[] | null
): { message: string; suggestion?: string } | null {
  // If we have AAA errors, process the first one
  if (errors && errors.length > 0) {
    const firstError = errors[0];
    return getEligibilityErrorMessage(firstError.code, firstError.description);
  }

  // If we have an error_message string, try to map it
  if (errorMessage) {
    return getEligibilityErrorMessage(null, errorMessage);
  }

  return null;
}
